import React, { useEffect, useRef, useState } from 'react';
import { RotateCcw } from 'lucide-react';

/**
 * Reusable OTP resend control.
 * - Forces a cool-down (default 60 s) before the customer can tap "Resend"
 * - Caps total resends per session (default 3) to protect the MSG91 throttle
 *   which was tripping impatient Jio users into IP-level blocks.
 *
 * Props:
 *   onResend        () => Promise<void>  — actual send function
 *   cooldownSec     number  (default 60)
 *   maxResends      number  (default 3)
 *   autoStart       boolean (default true)  — begin the first cool-down as
 *                   soon as the component mounts (i.e. the initial OTP has
 *                   just been sent). Set to false if you want the button
 *                   immediately clickable.
 *   className, size, variant — passed through to styling
 */
export const OtpResendButton = ({
  onResend,
  cooldownSec = 60,
  maxResends = 3,
  autoStart = true,
  className = '',
  variant = 'light', // 'light' (dark theme cards) | 'dark' (light theme)
  testId = 'otp-resend-btn',
}) => {
  const [remaining, setRemaining] = useState(autoStart ? cooldownSec : 0);
  const [count, setCount] = useState(0);
  const [sending, setSending] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (remaining <= 0) {
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
      return undefined;
    }
    timerRef.current = setInterval(() => {
      setRemaining((r) => (r <= 1 ? 0 : r - 1));
    }, 1000);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [remaining]);

  const tappedOut = count >= maxResends;
  const disabled = sending || remaining > 0 || tappedOut;

  const handleClick = async () => {
    if (disabled) return;
    setSending(true);
    try {
      await onResend();
      setCount((c) => c + 1);
      setRemaining(cooldownSec);
    } finally {
      setSending(false);
    }
  };

  let label;
  if (tappedOut) label = `Resend limit reached (${maxResends}/${maxResends})`;
  else if (sending) label = 'Sending…';
  else if (remaining > 0) label = `Resend in ${remaining}s`;
  else label = count === 0 ? 'Resend OTP' : `Resend OTP (${count}/${maxResends})`;

  const base =
    variant === 'light'
      ? // "light" = for light-themed modals (e.g. Landing sign-in): dark text on light background
        'w-full py-2 rounded-full border font-medium uppercase tracking-wide text-xs transition ' +
        (disabled
          ? 'border-[#1A1A1A]/15 text-[#1A1A1A]/40 bg-white cursor-not-allowed'
          : 'border-[#1A1A1A]/25 text-[#1A1A1A] bg-white hover:bg-[#FAF6EE]')
      : // "dark" = for dark-bordered card modals (e.g. DeliveryOtpModal, Admin panels)
        'inline-flex items-center gap-1 px-3 py-2 text-xs font-mono font-bold uppercase border-2 border-black transition ' +
        (disabled
          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
          : 'bg-white text-black hover:bg-black hover:text-white');

  return (
    <button
      type="button"
      data-testid={testId}
      onClick={handleClick}
      disabled={disabled}
      className={`${base} ${className}`}
    >
      {variant === 'dark' && <RotateCcw size={14} />}
      {label}
    </button>
  );
};

export default OtpResendButton;
